// frontend/src/pages/admin/AdminStores.js
import React, { useState, useEffect, useMemo, useRef } from 'react'; 
import { storesApi } from '../../api/stores';
import { zonesApi } from '../../api/zones';
import { usersApi } from '../../api/users';
import { walletApi } from '../../api/wallet';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSpinner, FaUniversity, FaCheckCircle, FaStore, FaUserCog, FaMapMarkerAlt, FaUserPlus, FaChevronDown, FaSearch } from 'react-icons/fa'; 

const initialFormState = {
    store_name: '',
    address: '',
    phone: '',
    email: '',
    state: '', 
    lga_id: '',
    zone_id: '',
    manager_user_id: '',
    is_active: true,
    allow_manager_updates: false, // <--- RESTORED
    allow_discount_management: false, // <--- RESTORED
    bank_code: '',
    account_number: '',
    new_manager_name: '',
    new_manager_email: '', 
    new_manager_password: '',
    new_manager_phone: '' 
};

function AdminStores() {
    // ... (Keep existing state and fetch logic) ...
    const [stores, setStores] = useState([]);
    const [zones, setZones] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [users, setUsers] = useState([]);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStore, setEditingStore] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    const [filteredZones, setFilteredZones] = useState([]);
    const [isQuickRegister, setIsQuickRegister] = useState(true); 
    const [isBankListOpen, setIsBankListOpen] = useState(false);
    const [bankSearchTerm, setBankSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [storesData, zonesData, lgasData, usersData, banksData] = await Promise.all([
                    storesApi.getAllStores(),
                    zonesApi.getAllZones(),
                    zonesApi.getAllLgas(),
                    usersApi.getAllUsersAdmin(),
                    walletApi.getBanks()
                ]);
                setStores(storesData);
                setZones(zonesData);
                setLgas(lgasData);
                setUsers(usersData);
                setBanks(banksData || []);
            } catch (err) {
                setError("Failed to load data. Please try again.");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsBankListOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (formData.lga_id) {
            const relevantZones = zones.filter(z => z.lga_id === formData.lga_id);
            setFilteredZones(relevantZones);
        } else {
            setFilteredZones([]);
        }
    }, [formData.lga_id, zones]);

    const groupedLgas = useMemo(() => {
        return lgas.reduce((acc, lga) => {
            if (!lga.state || !lga.lga) return acc;
            const stateKey = lga.state.trim();
            if (!acc[stateKey]) acc[stateKey] = [];
            acc[stateKey].push(lga);
            return acc;
        }, {});
    }, [lgas]);

    const availableLgas = formData.state ? groupedLgas[formData.state] || [] : [];

    const handleOpenModal = (store = null) => {
        if (store) {
            setEditingStore(store);
            setFormData({
                ...initialFormState,
                store_name: store.store_name,
                address: store.address,
                phone: store.phone,
                email: store.email,
                state: store.state || '', 
                lga_id: store.lga_id,
                zone_id: store.zone_id || '',
                manager_user_id: store.manager_user_id || '',
                is_active: store.is_active,
                allow_manager_updates: store.allow_manager_updates, // <--- LOADED
                allow_discount_management: store.allow_discount_management, // <--- LOADED
                bank_code: store.bank_code || '',
                account_number: store.account_number || ''
            });
            setIsQuickRegister(false); 
        } else {
            setEditingStore(null);
            setFormData(initialFormState);
            setIsQuickRegister(true); 
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingStore(null);
        setFormData(initialFormState);
        setIsQuickRegister(true);
        setError(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'state') {
            setFormData(prev => ({ ...prev, state: value, lga_id: '', zone_id: '' }));
        } else if (name === 'lga_id') {
            setFormData(prev => ({ ...prev, lga_id: value, zone_id: '' }));
        } else if (name === 'manager_user_id') {
            if (value) setIsQuickRegister(false);
            setFormData(prev => ({ ...prev, manager_user_id: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };
    
    const handleSelectBank = (bank) => {
        setFormData(prev => ({
            ...prev,
            bank_code: bank.code,
        }));
        setIsBankListOpen(false);
        setBankSearchTerm('');
    };

    const getBankName = (code) => {
        const bank = banks.find(b => b.code === code);
        return bank ? bank.name : 'Select Bank';
    };

    const filteredBanks = banks.filter(bank => 
        bank.name.toLowerCase().includes(bankSearchTerm.toLowerCase())
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.state) { alert("Please select a State."); return; }
        if (!editingStore) {
            if (isQuickRegister && (!formData.new_manager_password || !formData.new_manager_name)) {
                alert("Please complete all Quick Register fields.");
                return;
            }
            if (!isQuickRegister && !formData.manager_user_id) {
                alert("Please select an existing manager or use Quick Register.");
                return;
            }
        }
        if (isQuickRegister && !editingStore && (!formData.email || !formData.phone)) {
            alert("The Store's Email and Phone are required for Quick Registration.");
            return;
        }

        setSaving(true);
        setError(null);

        const payload = { ...formData };
        
        if (!editingStore) {
            if (!isQuickRegister) {
                delete payload.new_manager_password;
                delete payload.new_manager_name;
                delete payload.new_manager_email;
                delete payload.new_manager_phone;
                if (!payload.manager_user_id) delete payload.manager_user_id;
            } else {
                payload.new_manager_email = payload.email;
                payload.new_manager_phone = payload.phone;
                delete payload.manager_user_id;
            }
        } else {
            delete payload.new_manager_password;
            delete payload.new_manager_name;
            delete payload.new_manager_email;
            delete payload.new_manager_phone;
        }

        if (!payload.zone_id) delete payload.zone_id;
        if (!payload.bank_code) delete payload.bank_code;
        if (!payload.account_number) delete payload.account_number;
        if (!payload.store_name) delete payload.store_name;
        if (!payload.email) delete payload.email;
        
        delete payload.new_manager_email;
        delete payload.new_manager_password;
        delete payload.new_manager_name;
        delete payload.new_manager_phone;

        try {
            if (editingStore) {
                await storesApi.updateStore(editingStore.id, payload);
            } else {
                await storesApi.createStore(payload);
            }
            const updatedStores = await storesApi.getAllStores();
            setStores(updatedStores);
            handleCloseModal();
        } catch (err) {
            console.error(err);
            setError(err.data?.message || `Failed to save store.`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (storeId) => {
        if (window.confirm("Are you sure you want to delete this store?")) {
            try {
                await storesApi.deleteStore(storeId);
                setStores(prev => prev.filter(s => s.id !== storeId));
            } catch (err) {
                alert(err.data?.message || "Failed to delete store.");
            }
        }
    };

    if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Store Data...</div>;

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-2xl font-bold text-gray-800">Store Management</h1>
                <button 
                    onClick={() => handleOpenModal()} 
                    className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                    <FaPlus /> Add New Store
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stores.map(store => (
                    <div key={store.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-3">
                                <div className="bg-blue-50 p-3 rounded-full text-blue-600">
                                    <FaStore />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 text-lg">{store.store_name}</h3>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${store.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {store.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handleOpenModal(store)} className="text-gray-400 hover:text-blue-600"><FaEdit /></button>
                                <button onClick={() => handleDelete(store.id)} className="text-gray-400 hover:text-red-600"><FaTrash /></button>
                            </div>
                        </div>

                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                            <p className="flex items-center gap-2"><FaMapMarkerAlt className="text-gray-400" /> {store.lga_name}, {store.state}</p>
                            <p className="pl-6 text-xs">{store.address}</p>
                            <p className="flex items-center gap-2"><FaUserCog className="text-gray-400" /> {store.manager_name || 'No Manager Assigned'}</p>
                        </div>

                        <div className="border-t pt-3 flex flex-wrap gap-2 justify-between items-center">
                            <div className="flex gap-2">
                                {store.allow_manager_updates && <span className="text-[10px] bg-gray-100 px-2 py-1 rounded">Can Edit Profile</span>}
                            </div>
                            {store.subaccount_id ? (
                                <span className="text-[10px] bg-green-50 text-green-700 px-2 py-1 rounded font-bold flex items-center gap-1">
                                    <FaCheckCircle /> Payouts Active
                                </span>
                            ) : (
                                <span className="text-[10px] bg-yellow-50 text-yellow-700 px-2 py-1 rounded font-bold">
                                    No Bank Info
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-xl font-bold">{editingStore ? 'Edit Store' : 'Create New Store'}</h3>
                            <button onClick={handleCloseModal}><FaTimes /></button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
                            {error && <div className="bg-red-100 text-red-700 p-3 rounded text-sm">{error}</div>}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Store Name *</label>
                                    <input name="store_name" value={formData.store_name} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Phone *</label>
                                    <input name="phone" value={formData.phone} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Email *</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Address *</label>
                                    <input name="address" value={formData.address} onChange={handleInputChange} required className="p-2 border rounded w-full" />
                                </div>
                            </div>

                            {/* Location & Manager */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded border">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">State *</label>
                                    <select name="state" value={formData.state} onChange={handleInputChange} required className="p-2 border rounded w-full bg-white">
                                        <option value="">Select State</option>
                                        {Object.keys(groupedLgas).sort().map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">LGA *</label>
                                    <select name="lga_id" value={formData.lga_id} onChange={handleInputChange} required disabled={!formData.state} className="p-2 border rounded w-full bg-white disabled:bg-gray-100">
                                        <option value="">Select LGA</option>
                                        {availableLgas.map(lga => (
                                            <option key={lga.id} value={lga.id}>{lga.lga}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Zone (Optional)</label>
                                    <select name="zone_id" value={formData.zone_id} onChange={handleInputChange} className="p-2 border rounded w-full bg-white" disabled={!formData.lga_id}>
                                        <option value="">Select Zone</option>
                                        {filteredZones.map(z => (
                                            <option key={z.id} value={z.id}>{z.zone_name}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Manager</label>
                                    {!editingStore && (
                                        <button 
                                            type="button"
                                            onClick={() => setIsQuickRegister(!isQuickRegister)}
                                            className={`w-full py-2 rounded text-xs font-bold flex items-center justify-center gap-2 transition-colors mb-2 ${isQuickRegister ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                                        >
                                            {isQuickRegister ? 'Select Existing Manager' : 'Quick Register New Manager'}
                                        </button>
                                    )}
                                    {!isQuickRegister && (
                                        <select 
                                            name="manager_user_id" 
                                            value={formData.manager_user_id} 
                                            onChange={handleInputChange} 
                                            className="p-2 border rounded w-full bg-white"
                                            disabled={!!editingStore}
                                        >
                                            <option value="">Select Existing User</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>
                            
                            {isQuickRegister && !editingStore && (
                                <div className="bg-blue-100 p-4 rounded border border-blue-200 animate-fadeInUp">
                                    <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                                        <FaUserPlus /> Quick Register New Manager
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Full Name *</label>
                                            <input name="new_manager_name" value={formData.new_manager_name} onChange={handleInputChange} required={isQuickRegister} className="w-full p-2 border rounded" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Password *</label>
                                            <input type="password" name="new_manager_password" value={formData.new_manager_password} onChange={handleInputChange} required={isQuickRegister} minLength={6} className="w-full p-2 border rounded" />
                                        </div>
                                        <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                            <p className="text-xs font-bold text-yellow-800">Manager's Email/Phone</p>
                                            <p className="text-xs text-yellow-700 mt-1">
                                                Automatically uses the Store's Email/Phone from above for the new user's account.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Financials */}
                            <div className="bg-green-50 p-4 rounded border border-green-200">
                                <h4 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                                    <FaUniversity /> Payout Details (Split Payments)
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Bank Name</label>
                                        <div className="relative" ref={dropdownRef}>
                                            <div 
                                                className="w-full p-2 border rounded bg-white flex justify-between items-center cursor-pointer"
                                                onClick={() => setIsBankListOpen(!isBankListOpen)}
                                            >
                                                <span className={formData.bank_code ? "text-gray-800" : "text-gray-400"}>
                                                    {getBankName(formData.bank_code) || "Select Bank"}
                                                </span>
                                                <FaChevronDown className="text-gray-400 text-xs" />
                                            </div>

                                            {isBankListOpen && (
                                                <div className="absolute z-50 w-full bg-white border border-gray-300 rounded shadow-lg mt-1 max-h-40 overflow-hidden flex flex-col">
                                                    <div className="p-2 border-b bg-gray-50">
                                                        <div className="flex items-center bg-white border rounded px-2">
                                                            <FaSearch className="text-gray-400" />
                                                            <input 
                                                                type="text"
                                                                className="w-full p-2 outline-none text-sm"
                                                                placeholder="Search bank..."
                                                                value={bankSearchTerm}
                                                                onChange={(e) => setBankSearchTerm(e.target.value)}
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="overflow-y-auto flex-grow">
                                                        {filteredBanks.length > 0 ? (
                                                            filteredBanks.map(bank => (
                                                                <div 
                                                                    key={bank.code}
                                                                    onClick={() => handleSelectBank(bank)}
                                                                    className="p-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 border-b border-gray-50 last:border-0"
                                                                >
                                                                    {bank.name}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="p-4 text-center text-gray-500 text-sm">No banks found.</div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-600 mb-1">Account Number</label>
                                        <input name="account_number" value={formData.account_number} onChange={handleInputChange} className="p-2 border rounded w-full" placeholder="10-digit NUBAN" maxLength={10} />
                                    </div>
                                </div>
                            </div>

                            {/* --- RESTORED CHECKBOXES --- */}
                            <div className="space-y-2 bg-gray-50 p-4 rounded border border-gray-200">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="h-4 w-4 text-primary rounded" />
                                    <span className="text-sm text-gray-700 font-bold">Store is Active</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" name="allow_manager_updates" checked={formData.allow_manager_updates} onChange={handleInputChange} className="h-4 w-4 text-primary rounded" />
                                    <span className="text-sm text-gray-700">Allow Manager to Edit Profile / Items</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" name="allow_discount_management" checked={formData.allow_discount_management} onChange={handleInputChange} className="h-4 w-4 text-primary rounded" />
                                    <span className="text-sm text-gray-700">Allow Manager to Apply Discounts</span>
                                </label>
                            </div>
                            {/* --------------------------- */}

                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-600">Cancel</button>
                                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark">
                                    {saving ? 'Saving...' : 'Save Store'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminStores;